<template>
    <TransitionFade>
        <div v-if="isOpen"
             class="fixed z-51 h-screen w-screen overflow-y-hidden backdrop-blur-[1px]"
             style="background-color: rgba(0,0,0, 0.1)"
             @click="close" />
    </TransitionFade>
    <div class="editor fixed top-0 right-0 z-51 h-full overflow-y-auto bg-base-100 px-16 py-8 shadow"
         :class="{ isOpen }"
         role="dialog"
         aria-modal="true"
         aria-labelledby="ingredient-editor-title"
         :inert="!isOpen">
        <div class="text-color-muted mb-12 flex items-center justify-between">
            <h1 id="ingredient-editor-title">
                Ingredient Editor
            </h1>
            <button class="btn btn-square btn-ghost btn-sm"
                    title="Close editor"
                    aria-label="Close editor"
                    @click="isOpen = false">
                <Icon name="mdi:close" class="size-6" />
            </button>
        </div>
        <div ref="container" class="relative space-y-6">
            <div ref="nameContainer" class="h-full items-start">
                <label for="name" class="label inline-flex">
                    Name
                </label>
                <input id="name"
                       ref="nameInput"
                       v-model="ingredient.name"
                       autocomplete="off"
                       type="text"
                       required
                       class="input w-full bg-base-200/30 input-lg"
                       @click="dropdownOpen = true">
                <details class="dropdown z-10 w-full"
                         :open="!!potentialMatches.length && dropdownOpen">
                    <summary class="hidden" />
                    <ul class="menu dropdown-content mt-2 max-h-48 w-full flex-nowrap overflow-y-auto
                               rounded-box bg-base-100 p-2 shadow">
                        <li v-for="(ingr, index) in potentialMatches"
                            :key="index"
                            tabindex="0"
                            class="cursor-pointer rounded px-4 py-3 transition-colors hover:bg-base-200"
                            @keyup.enter="updateIngr(ingr)"
                            @click="updateIngr(ingr)">
                            {{ ingr.name }}
                        </li>
                    </ul>
                </details>
            </div>
            <TransitionExpand>
                <div v-show="!ingredient.isCompound">
                    <label class="label cursor-pointer justify-start gap-2">
                        <input v-model="ingredient.isAllergen"
                               type="checkbox"
                               class="checkbox">
                        Is An Allergen
                    </label>
                </div>
            </TransitionExpand>
            <div>
                <label class="label cursor-pointer justify-start gap-2">
                    <input v-model="ingredient.isHomemade"
                           type="checkbox"
                           class="checkbox">
                    Is Homemade
                    <span class="tooltip tooltip-top inline-flex flex-col justify-center"
                          data-tip="Mark this ingredient as homemade.">
                        <Icon name="mdi:help-circle-outline" class="ml-1 size-5" />
                    </span>
                </label>
            </div>
            <div>
                <label class="label cursor-pointer justify-start gap-2">
                    <input v-model="ingredient.isCompound"
                           type="checkbox"
                           class="checkbox"
                           @change="ingredient.isAllergen = false">
                    Is Compound
                    <span class="tooltip tooltip-top inline-flex flex-col justify-center"
                          data-tip="Compound ingredients are made from other ingredients. Cost is calculated automatically.">
                        <Icon name="mdi:help-circle-outline" class="ml-1 size-5" />
                    </span>
                </label>
            </div>
            <div v-if="!isCostAutoCalculated" class="items-start">
                <label for="cost" class="label inline-flex">
                    Cost
                </label>
                <div class="join w-full">
                    <label for="cost" class="join-item border border-base-300 bg-base-300 px-4 py-2.5 select-none">
                        £
                    </label>
                    <input id="cost"
                           v-model="ingredient.cost"
                           type="number"
                           min="0.00"
                           :required="isCostRequired"
                           step="0.01"
                           pattern="^[0-9]+(\.[0-9]{1,2})?$"
                           class="input join-item w-full bg-base-200/30 input-lg invalid:input-error">
                </div>
            </div>
            <div v-if="isCostAutoCalculated" class="alert alert-info">
                <Icon name="mdi:information-outline" class="size-5" />
                <span>Cost will be calculated automatically based on sub-ingredients</span>
            </div>
            <div class="items-start">
                <label for="name" class="label inline-flex">
                    Unit
                    <span class="tooltip tooltip-top inline-flex flex-col justify-center"
                          data-tip="The unit the ingredient is measured in.">
                        <Icon name="mdi:help-circle-outline" class="ml-1 size-5" />
                    </span>
                </label>
                <select v-model="ingredient.unitId"
                        class="select w-full bg-base-200/30 select-lg"
                        required
                        placeholder="Please select a unit.">
                    <optgroup label="Absolute units">
                        <option v-for="aUnit in unitStore.absoluteUnits"
                                :key="aUnit.id"
                                :selected="aUnit.id === aUnit.id"
                                :value="aUnit.id"
                                v-text="aUnit.name ?? aUnit.abbreviation" />
                    </optgroup>
                    <optgroup label="Relative units">
                        <option v-for="rUnit in unitStore.relativeUnits"
                                :key="rUnit.id"
                                :selected="rUnit.id === rUnit.id"
                                :value="rUnit.id"
                                v-text="rUnit.name ?? rUnit.abbreviation" />
                    </optgroup>
                </select>
            </div>
            <div class="items-start">
                <label for="amount" class="label inline-flex">
                    Unit Amount
                </label>
                <div class="join w-full">
                    <input id="amount"
                           v-model="ingredient.unitAmount"
                           type="number"
                           min="0.01"
                           required
                           step="1"
                           placeholder="Quantity of the unit type."
                           :class="{ 'join-item': !!suffix }"
                           class="input w-full bg-base-200/30 input-lg">
                    <TransitionExpand axis="x">
                        <label v-if="suffix" for="amount" class="join-item border bg-base-300 px-4 py-2.5 select-none">
                            {{ suffix }}
                        </label>
                    </TransitionExpand>
                </div>
            </div>

            <!-- Sub-ingredients selector -->
            <div v-if="ingredient.isCompound" class="items-start">
                <label class="label inline-flex">
                    Sub-Ingredients
                    <span class="tooltip tooltip-top inline-flex flex-col justify-center"
                          data-tip="Select at least 2 ingredients that make up this compound ingredient.">
                        <Icon name="mdi:help-circle-outline" class="ml-1 size-5" />
                    </span>
                </label>

                <!-- Search and add sub-ingredient -->
                <div class="w-full space-y-2">
                    <div ref="subIngredientContainer" class="relative">
                        <input v-model="subIngredientSearch"
                               type="text"
                               placeholder="Search ingredients to add..."
                               class="input w-full bg-base-200/30"
                               @focus="subIngredientDropdownOpen = true">
                        <details class="dropdown z-10 w-full"
                                 :open="!!availableSubIngredients.length && subIngredientDropdownOpen">
                            <summary class="hidden" />
                            <ul class="menu dropdown-content mt-2 max-h-48 w-full flex-nowrap overflow-y-auto
                                       rounded-box bg-base-100 p-2 shadow">
                                <li v-for="ingr in availableSubIngredients"
                                    :key="ingr.id"
                                    tabindex="0"
                                    class="cursor-pointer rounded px-4 py-3 transition-colors hover:bg-base-200"
                                    @keyup.enter="addSubIngredient(ingr)"
                                    @click="addSubIngredient(ingr)">
                                    <div class="flex items-center justify-between">
                                        <span>{{ ingr.name }}</span>
                                        <span v-if="ingr.cost && ingr.unitAmount" class="text-sm opacity-70">
                                            £{{ (ingr.cost / ingr.unitAmount).toFixed(4) }}/{{ unitStore.units.find(u => u.id === ingr.unitId)?.abbreviation }}
                                        </span>
                                    </div>
                                </li>
                            </ul>
                        </details>
                    </div>

                    <!-- Selected sub-ingredients list -->
                    <div v-if="selectedSubIngredients.length" class="space-y-2 rounded-lg border border-base-300 p-4">
                        <div v-for="(subIngr, index) in selectedSubIngredients"
                             :key="subIngr.id"
                             class="flex items-center gap-2 rounded-lg bg-base-200/50 p-3">
                            <div class="flex-1">
                                <div class="font-medium">
                                    {{ subIngr.name }}
                                </div>
                                <div v-if="subIngr.cost && subIngr.unitAmount" class="text-sm opacity-70">
                                    £{{ (subIngr.cost / subIngr.unitAmount).toFixed(4) }}/{{ unitStore.units.find(u => u.id === subIngr.unitId)?.abbreviation }}
                                </div>
                            </div>
                            <div class="flex items-center gap-2">
                                <input v-model.number="subIngr.amount"
                                       type="number"
                                       min="0.01"
                                       step="0.01"
                                       required
                                       placeholder="Amount"
                                       class="input w-24 bg-base-100 input-sm">
                                <span class="text-sm opacity-70">{{ unitStore.units.find(u => u.id === subIngr.unitId)?.abbreviation }}</span>
                                <button type="button"
                                        class="btn btn-circle btn-ghost btn-sm"
                                        title="Remove sub-ingredient"
                                        @click="removeSubIngredient(index)">
                                    <Icon name="mdi:close" class="size-5" />
                                </button>
                            </div>
                        </div>

                        <div v-if="estimatedCost > 0" class="mt-3 border-t border-base-300 pt-3 text-right">
                            <span class="text-sm opacity-70">Estimated cost: </span>
                            <span class="font-semibold">£{{ estimatedCost.toFixed(2) }}</span>
                        </div>
                    </div>

                    <div v-if="ingredient.isCompound && selectedSubIngredients.length < 2"
                         class="text-sm text-error">
                        At least 2 sub-ingredients are required for compound ingredients
                    </div>
                </div>
            </div>
        </div>
        <div class="mt-6 flex flex-row justify-end">
            <button :disabled="loading"
                    :class="[isCreatingIngredient ? 'btn-success' : 'btn-info']"
                    class="btn mt-8 px-8!"
                    @click="save">
                {{ isCreatingIngredient ? 'Create' : 'Update' }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { plural } from '@upfrontjs/framework';
import type { IngredientAttributes, SubIngredient, UnitAttributes } from '~/types';

const api = useApi();

defineOptions({
    name: 'IngredientEditor'
});

const loading = ref(false);
const ingredientStore = useIngredientStore();
const unitStore = useUnitStore();
const isOpen = ref(false);
const nameInput = ref<HTMLInputElement>();
let returnFocusTo: HTMLElement | null = null;

watch(isOpen, async open => {
    if (open) {
        returnFocusTo = document.activeElement as HTMLElement | null;
        await nextTick();
        nameInput.value?.focus();
    } else {
        returnFocusTo?.focus();
        returnFocusTo = null;
    }
});
onKeyStroke('Escape', () => {
    if (isOpen.value) isOpen.value = false;
});
const ingredient = ref<IngredientAttributes>({
    id: NaN,
    isAllergen: false,
    name: '',
    unitAmount: NaN,
    cost: NaN,
    unitId: NaN,
    isHomemade: false,
    isCompound: false,
    subIngredients: []
});

// Sub-ingredients state
const subIngredientSearch = ref('');
const subIngredientDropdownOpen = ref(false);
const subIngredientContainer = ref<HTMLDivElement>();
const selectedSubIngredients = ref<SubIngredient[]>([]);
const unit = computed<UnitAttributes | undefined>(() => {
    return unitStore.units.find(u => u.id === ingredient.value.unitId);
});
const isCreatingIngredient = computed(() => isNaN(ingredient.value.id));

// Cost is auto-calculated for all compound ingredients
const isCostAutoCalculated = computed(() => {
    return ingredient.value.isCompound;
});
watch(isCostAutoCalculated, newVal => {
    if (newVal) {
        ingredient.value.cost = NaN;
    }
});

// Cost is required only for non-compound ingredients
const isCostRequired = computed(() => {
    return !ingredient.value.isCompound;
});
const suffix = computed(() => {
    if (!ingredient.value.unitAmount || !unit.value) {
        return '';
    }

    if (ingredient.value.unitAmount <= 1) {
        return unit.value.name;
    }

    return unitStore.relativeUnits.map(u => u.abbreviation).includes(unit.value.abbreviation)
        ? unit.value.name ?? unit.value.abbreviation
        : unit.value.name ? plural(unit.value.name) : unit.value.abbreviation;
});
const potentialMatches = computed<IngredientAttributes[]>(() => {
    const typedName = ingredient.value.name.trim();
    if (!typedName) {
        return [];
    }

    const matches = ingredientStore.ingredients.filter(
        i => i.name.toLowerCase().includes(typedName.toLowerCase())
    );

    // Check if an exact match already exists
    const exactMatchExists = matches.some(
        i => i.name.toLowerCase() === typedName.toLowerCase()
    );

    if (exactMatchExists) {
        // Move the exact match to the front
        const exactMatch = matches.find(i => i.name.toLowerCase() === typedName.toLowerCase())!;
        const others = matches.filter(i => i.name.toLowerCase() !== typedName.toLowerCase());
        return [exactMatch, ...others];
    }

    // Add the typed name as the first option for creating a new ingredient
    const newEntry: IngredientAttributes = {
        id: NaN,
        name: typedName,
        isAllergen: false,
        unitAmount: NaN,
        cost: NaN,
        unitId: NaN,
        isHomemade: false,
        isCompound: false,
        subIngredients: []
    };
    return [newEntry, ...matches];
});
const dropdownOpen = ref(false);
const nameContainer = ref<HTMLDivElement>();

// Available sub-ingredients (filtered by search, excluding current ingredient and already selected)
const availableSubIngredients = computed<IngredientAttributes[]>(() => {
    return ingredientStore.ingredients.filter(ingr => {
        // Exclude current ingredient (prevent self-reference)
        if (!isNaN(ingredient.value.id) && ingr.id === ingredient.value.id) {
            return false;
        }

        // Exclude already selected sub-ingredients
        if (selectedSubIngredients.value.some(si => si.id === ingr.id)) {
            return false;
        }

        // Filter by search term
        if (subIngredientSearch.value) {
            return ingr.name.toLowerCase().includes(subIngredientSearch.value.toLowerCase());
        }

        return true;
    });
});

// Calculate estimated cost based on sub-ingredients
const estimatedCost = computed(() => {
    if (!ingredient.value.isCompound || !selectedSubIngredients.value.length) {
        return 0;
    }

    return selectedSubIngredients.value.reduce((total, subIngr) => {
        if (!subIngr.cost || !subIngr.unitAmount || !subIngr.amount) {
            return total;
        }

        const unitCost = subIngr.cost / subIngr.unitAmount;
        return total + (unitCost * subIngr.amount);
    }, 0);
});

onClickOutside(nameContainer, () => dropdownOpen.value = false);
onClickOutside(subIngredientContainer, () => subIngredientDropdownOpen.value = false);
const open = () => isOpen.value = true;
const close = () => isOpen.value = false;
const reset = () => {
    ingredient.value = {
        id: NaN,
        isAllergen: false,
        name: '',
        unitAmount: NaN,
        cost: NaN,
        unitId: NaN,
        isHomemade: false,
        isCompound: false,
        subIngredients: []
    };
    selectedSubIngredients.value = [];
    subIngredientSearch.value = '';
};

const addSubIngredient = (ingr: IngredientAttributes) => {
    selectedSubIngredients.value.push({
        ...ingr,
        amount: 1
    });
    subIngredientSearch.value = '';
    subIngredientDropdownOpen.value = false;
};

const removeSubIngredient = (index: number) => {
    selectedSubIngredients.value.splice(index, 1);
};
const openFresh = () => {
    reset();
    open();
};
const updateIngr = async (ingr: IngredientAttributes) => {
    dropdownOpen.value = false;

    if (ingr.isCompound && ingr.id) {
        const full = await api<IngredientAttributes>(`/ingredients/${ingr.id}`);
        ingredient.value = full;
        selectedSubIngredients.value = full.subIngredients?.length ? [...full.subIngredients] : [];
    } else {
        ingredient.value = ingr;
        selectedSubIngredients.value = [];
    }
};

const save = async () => {
    // Validate compound ingredient requirements
    if (ingredient.value.isCompound && selectedSubIngredients.value.length < 2) {
        useToast().error('Compound ingredients must have at least 2 sub-ingredients');
        return;
    }

    // Cost is required when the ingredient is not compound (isCompound === false)
    if (isCostRequired.value && (!ingredient.value.cost || ingredient.value.cost <= 0)) {
        useToast().error('Cost is required for this ingredient');
        return;
    }

    loading.value = true;

    // Prepare the body - sync selectedSubIngredients with ingredient.subIngredients
    const body = {
        ...ingredient.value,
        subIngredients: ingredient.value.isCompound ? selectedSubIngredients.value : []
    };

    return api<IngredientAttributes>(
        isCreatingIngredient.value ? '/ingredients' : `/ingredients/${ingredient.value.id}`,
        {
            method: isCreatingIngredient.value ? 'POST' : 'PATCH',
            body
        })
        .then(ingredient => {
            if (isCreatingIngredient.value) {
                ingredientStore.ingredients.push(ingredient);
                return;
            }

            ingredientStore.ingredients = ingredientStore.ingredients.map(ingr => {
                if (ingr.id === ingredient.id) {
                    // retain product count
                    ingr = { ...ingr, ...ingredient };
                }

                return ingr;
            });
            return;
        })
        .then(() => {
            useToast().success(`Ingredient ${isCreatingIngredient.value ? 'created' : 'updated'}.`);
            close();
            return;
        })
        .catch(reason => {
            const handler = parseError(reason);

            if (handler.isValidationError) {
                useToast().error(handler.errorMessage);
                return;
            }
        })
        .finally(() => loading.value = false);
};

const editIngredient = async (attributes: IngredientAttributes) => {
    // Fetch full data for compound ingredients to get sub-ingredient details
    if (attributes.isCompound && attributes.id) {
        const full = await api<IngredientAttributes>(`/ingredients/${attributes.id}`);
        ingredient.value = full;
        selectedSubIngredients.value = full.subIngredients?.length ? [...full.subIngredients] : [];
    } else {
        ingredient.value = attributes;
        selectedSubIngredients.value = [];
    }

    open();
};

defineExpose({
    editIngredient,
    open,
    close,
    openFresh,
    isOpen
});
</script>

<style scoped>
@reference "~/assets/css/tailwind.css";
.editor {
    width: 100vw;
    visibility: hidden;
    transform: translateX(100%);
    transition: transform 250ms ease-in, visibility 0s linear 250ms;

    &.isOpen {
        visibility: visible;
        transform: translateX(0);
        transition: transform 250ms cubic-bezier(0.16, 1, 0.3, 1), visibility 0s;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;

        &.isOpen {
            transition: none;
        }
    }

    /* stylelint-disable media-query-no-invalid */
    @media (min-width: theme(--breakpoint-sm)) {
        width: 80vw;
    }

    @media (min-width: theme(--breakpoint-md)) {
        width: 60vw;
    }

    @media (min-width: theme(--breakpoint-lg)) {
        width: 50vw;
    }

    @media (min-width: theme(--breakpoint-xl)) {
        width: 45vw;
    }

    @media (min-width: theme(--breakpoint-2xl)) {
        width: 35vw;
    }
    /* stylelint-enable media-query-no-invalid */
}
</style>
